import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'ui-components';
import { ALL_SOCIAL_PLATFORM_TYPE, SOCIAL_PLATFORMS } from '~/constants';
import { FEATURE_FLAGS } from '~/constants/featureFlags';
import { trackEvent } from '~/core/utils/trackEvent';
import { useGetContent } from '~/hooks/api/useGetContent';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { TTAContent } from '~/types/tta';

import { Carousel } from './components/Carousel';
import { ContentFooter } from './components/ContentFooter';
import { ContentInfo } from './components/ContentInfo';
import { ContentItem } from './components/ContentItem';
import { MetricsSection } from './components/MetricsSection';
import { SelectedContent } from './components/SelectedContent';
import { mentionCountRanges } from './utils/mentionCount';

interface Props {
  ttaContent?: TTAContent[];
}

export const TTAPostSnippet: React.FC<Props> = ({ ttaContent }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedContent = ttaContent?.[selectedIndex] ?? ttaContent?.[0];
  const { data: selectedContentData } = useGetContent(
    selectedContent?.content_id ?? '',
  );
  const numberOfTimes = ttaContent?.length ?? 0;
  const contentData = selectedContentData?.data[0];

  const { isEnabled: isMentionCountRangesEnabled } = useFeature(
    FEATURE_FLAGS.foamMentionCountRanges,
  );

  const toggleCollapse = () => {
    const newState = !isOpen;
    setIsOpen(newState);

    // Track expand/collapse events
    trackEvent('foam-tta-post-snippet-toggle', {
      action: newState ? 'expand' : 'collapse',
      numberOfPosts: numberOfTimes,
      selectedContentId: selectedContent?.content_id || 'unknown',
      platform: contentData?.platform || 'unknown',
    });
  };

  const handleCarouselSelect = (index: number) => {
    const previousIndex = selectedIndex;
    setSelectedIndex(index);

    // Track carousel item selection
    trackEvent('foam-tta-post-snippet-carousel-select', {
      selectedIndex: index,
      previousIndex,
      totalItems: numberOfTimes,
      selectedContentId: ttaContent?.[index]?.content_id || 'unknown',
      navigationDirection: index > previousIndex ? 'forward' : 'backward',
    });
  };

  const IconComponent = isOpen ? ChevronUpIcon : ChevronDownIcon;

  const platform: ALL_SOCIAL_PLATFORM_TYPE = (contentData?.platform ??
    SOCIAL_PLATFORMS.instagram) as ALL_SOCIAL_PLATFORM_TYPE;

  return (
    <div className="flex flex-col items-end tab:ml-[176px] mac:ml-[152px]">
      <div className="flex w-full flex-col items-start gap-3 rounded-8 bg-neutral-25 p-3 pt-4 shadow-sm">
        <div className="flex w-full items-center justify-between">
          <div className="truncate text-16 font-400 text-root-text-color-strong">
            {`Mentioned in`}
            {isMentionCountRangesEnabled ? (
              <span className="ml-1 text-16 font-500">
                {mentionCountRanges(numberOfTimes)}
              </span>
            ) : (
              <span className="ml-1 text-16 font-500">{`${numberOfTimes} ${
                numberOfTimes === 1 ? 'post' : 'posts'
              }`}</span>
            )}
          </div>
          <button
            onClick={toggleCollapse}
            aria-expanded={isOpen}
            type="button"
            className="flex items-center gap-2 text-neutral-700 hover:text-neutral-700"
          >
            <span className="text-14 font-500 text-neutral-700">
              {isOpen ? 'Close' : 'See all posts'}
            </span>
            <IconComponent className="shrink-0" boxSize={4} />
          </button>
        </div>
        <div className="w-full">
          {isOpen ? (
            <div className="flex h-full min-w-0 flex-col gap-4 tab:flex-row">
              {selectedContent && <SelectedContent content={selectedContent} />}
              <div className="flex min-w-0 flex-1 flex-col gap-4">
                {ttaContent && (
                  <div className="flex-none">
                    <Carousel
                      items={ttaContent}
                      selectedIndex={selectedIndex}
                      onSelect={handleCarouselSelect}
                    />
                  </div>
                )}
                <ContentInfo selectedContent={selectedContent} />
                <MetricsSection platform={platform} contentData={contentData} />
                <ContentFooter contentData={contentData} />
              </div>
            </div>
          ) : (
            selectedContent && (
              <ContentItem content={selectedContent} showInfo={true} />
            )
          )}
        </div>
      </div>
    </div>
  );
};
